import { z } from 'zod';
import { request } from './api-client';

/**
 * The account's sending reputation over time (spec § 4.6).
 *
 * Every score field is nullable, and that is the whole shape of this data rather than defensiveness:
 * SendGrid only scores a day where open tracking was on and the account sent 1,000+ messages in the
 * prior 30 days, so a quiet week legitimately has none. `.nullable()` here is what stops the UI
 * inventing a zero, which on a 1–5 scale would read as "as bad as it gets".
 */

export const SenderHealthPointSchema = z.object({
  day: z.string(),
  score: z.number().nullable(),
  engagementRecency: z.number().nullable(),
  openRate: z.number().nullable(),
  bounceClassification: z.number().nullable(),
  bounceRate: z.number().nullable(),
  spamRate: z.number().nullable(),
  fetchedAt: z.string(),
});

export type SenderHealthPoint = z.infer<typeof SenderHealthPointSchema>;

export const SenderHealthTrendSchema = z.object({
  points: z.array(SenderHealthPointSchema),
  /** The most recent day that HAS a score — not necessarily the most recent day. */
  latest: SenderHealthPointSchema.nullable(),
  /** Null when fewer than two days carry a score. A slope needs two points. */
  change: z.number().nullable(),
  scoredDays: z.number(),
  requestedDays: z.number(),
});

export type SenderHealthTrend = z.infer<typeof SenderHealthTrendSchema>;

export function fetchSenderHealth(days: number) {
  return request(`/api/v1/sender-health?days=${days}`, SenderHealthTrendSchema);
}

/** SendGrid's scale. Named rather than inlined so the axis and the bar heights cannot disagree. */
export const MAX_SCORE = 5;
